import type { Deal, DiligenceItem, DiligenceStatus, DiligenceSeverity } from "../types";
import { DILIGENCE_TEMPLATE, withDiligence, diligenceStats, isDefect } from "../lib/diligence";
import { money0 } from "../lib/format";

const STATUS_META: Record<DiligenceStatus, { cls: string; label: string }> = {
  todo: { cls: "t-watch", label: "To inspect" },
  pass: { cls: "t-pass", label: "Pass" },
  defect: { cls: "t-fail", label: "Defect" },
  monitor: { cls: "t-watch", label: "Monitor" },
};
const SEVERITIES: DiligenceSeverity[] = ["minor", "moderate", "major", "safety"];

let customSeq = 0;

/**
 * The property condition matrix — foolproof, systems × CSI, team-attributable.
 * Every "defect" with a remediation cost is documented leverage that flows into
 * the negotiation worksheet and the AI offer letter.
 */
export function Diligence({ deal, onChange }: { deal: Deal; onChange: (patch: Partial<Deal>) => void }) {
  const items = withDiligence(deal);
  const stats = diligenceStats(items);

  // Persist only touched items (status set, or any finding/cost/etc.) + custom.
  const touched = (d: DiligenceItem) =>
    d.status !== "todo" || !!d.finding || (d.cost ?? 0) > 0 || (d.attachments?.length ?? 0) > 0 || !!d.severity || !!d.source || d.id.startsWith("dd_custom");

  const commit = (next: DiligenceItem[]) => onChange({ diligence: next.filter(touched) });
  // A defect counts as leverage unless negotiable === false, so no defaulting needed.
  const patch = (id: string, p: Partial<DiligenceItem>) =>
    commit(items.map((it) => (it.id === id ? { ...it, ...p } : it)));
  const addCustom = (system: string, csi: string) => {
    customSeq += 1;
    const id = `dd_custom_${customSeq}_${performance.now().toString(36).replace(".", "")}`;
    commit([...items, { id, system, csi, label: "", status: "defect" }]);
  };
  const removeCustom = (id: string) => commit(items.filter((it) => it.id !== id));

  return (
    <>
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          The foolproof inspection matrix — every building system, cross-referenced to the code (CSI) divisions, plus the
          Florida insurance checks (4-point, wind-mit, WDO) and public records. Mark each item <b>Pass</b> or <b>Defect</b>;
          a defect with a repair cost becomes <b>documented leverage</b>. Your inspectors and GCs fill this from their own
          seats, so it updates automatically — then it feeds your revised offer and the AI letter to the seller.
        </p>
      </div>

      {/* SUMMARY */}
      <div className="card pad" style={{ marginBottom: 12, borderLeft: "4px solid var(--brass)" }}>
        <div className="grid g3">
          <div><div className="eyebrow">Inspected</div><div className="fr" style={{ fontSize: 22, color: "var(--forest)", fontWeight: 600 }}>{stats.inspected}/{stats.total}</div></div>
          <div><div className="eyebrow">Defects{stats.safety ? " · incl. safety" : ""}</div><div className="fr" style={{ fontSize: 22, color: stats.defects ? "var(--fail)" : "var(--forest)", fontWeight: 600 }}>{stats.defects}{stats.safety ? ` (${stats.safety}⚠)` : ""}</div></div>
          <div><div className="eyebrow">Documented leverage</div><div className="fr" style={{ fontSize: 22, color: "var(--brass-deep)", fontWeight: 600 }}>{money0(stats.leverage)}</div></div>
        </div>
        <div className="bar" style={{ height: 8, marginTop: 12 }}><span style={{ width: (stats.total ? (stats.inspected / stats.total) * 100 : 0) + "%" }} /></div>
        <div className="note" style={{ marginTop: 8 }}>
          Every dollar here is a documented, backed-up reduction to your offer — the negotiation worksheet totals it and caps your price at what still makes you a profit.
        </div>
      </div>

      {/* SYSTEMS */}
      {DILIGENCE_TEMPLATE.map((t) => {
        const rows = items.filter((d) => d.system === t.system);
        const insp = rows.filter((d) => d.status !== "todo").length;
        const defs = rows.filter(isDefect).length;
        return (
          <details key={t.system} className="card pad dd-sys" style={{ marginBottom: 8 }}>
            <summary className="dd-sum">
              <span className="dd-sys-name">{t.system} <span className="dd-csi">{t.csi}</span></span>
              <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {defs > 0 && <span className="tag t-fail">{defs} defect{defs > 1 ? "s" : ""}</span>}
                <span className="tag t-watch">{insp}/{rows.length}</span>
              </span>
            </summary>
            <div className="tablewrap" style={{ marginTop: 10 }}>
              <table className="t">
                <thead><tr><th>Point</th><th>Status</th><th>Finding</th><th className="n">Repair $</th><th>Sev.</th><th>Files</th><th></th></tr></thead>
                <tbody>
                  {rows.map((d) => {
                    const defect = d.status === "defect" || d.status === "monitor";
                    return (
                      <tr key={d.id}>
                        <td>
                          {d.id.startsWith("dd_custom")
                            ? <input className="rp-in" style={{ minWidth: 150 }} value={d.label} placeholder="custom point" onChange={(e) => patch(d.id, { label: e.target.value })} />
                            : <span style={{ fontWeight: 500, color: "var(--forest)" }}>{d.label}</span>}
                        </td>
                        <td>
                          <select className="rp-in" value={d.status} onChange={(e) => patch(d.id, { status: e.target.value as DiligenceStatus })}
                            style={{ borderColor: d.status === "defect" ? "var(--fail)" : d.status === "pass" ? "var(--pass)" : "var(--line)" }}>
                            <option value="todo">To inspect</option><option value="pass">Pass</option><option value="defect">Defect</option><option value="monitor">Monitor</option>
                          </select>
                        </td>
                        <td><input className="rp-in" style={{ minWidth: 150 }} value={d.finding ?? ""} placeholder={defect ? "what was observed" : "notes"} onChange={(e) => patch(d.id, { finding: e.target.value })} /></td>
                        <td className="n">{defect ? <input className="rp-in n" type="number" min={0} style={{ width: 90, textAlign: "right" }} value={d.cost || ""} placeholder="0" onChange={(e) => patch(d.id, { cost: Number(e.target.value) || 0 })} /> : <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td>{defect ? (
                          <select className="rp-in" value={d.severity ?? "moderate"} onChange={(e) => patch(d.id, { severity: e.target.value as DiligenceSeverity })}>
                            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td>
                          <label className="tw-file">📎<input type="file" multiple style={{ display: "none" }} onChange={(e) => { const names = Array.from(e.target.files ?? []).map((f) => f.name); if (names.length) patch(d.id, { attachments: [...(d.attachments ?? []), ...names] }); }} /></label>
                          {(d.attachments?.length ?? 0) > 0 && <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 3 }}>{d.attachments!.length}</span>}
                        </td>
                        <td>{d.id.startsWith("dd_custom") && <button className="rp-x" title="Remove" onClick={() => removeCustom(d.id)}>✕</button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="btn btn-g btn-sm noprint" style={{ marginTop: 8 }} onClick={() => addCustom(t.system, t.csi)}>+ Add point to {t.system}</button>
            {rows.filter((d) => isDefect(d) && d.severity === "safety").length > 0 && (
              <div className="flag bad" style={{ marginTop: 8 }}><span>!</span><div>Safety-level defect in {t.system} — flag it prominently in the offer letter.</div></div>
            )}
          </details>
        );
      })}
    </>
  );
}
